import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './styles/FAQ.scss'; // Assuming UK version uses the same styles

const FAQ_UK = () => {
  // State to track which FAQ items are expanded
  const [expandedItems, setExpandedItems] = useState({});

  // Toggle function for expanding/collapsing
  const toggleItem = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // List of FAQ questions and answers - UK Version
  const faqItems = [
    {
      question: "What is Pulc.ai?",
      answer: (
        <p>
          Pulc.ai is an AI-powered educational platform that helps students improve their essay writing skills and
          assists teachers with grading. Pulc trains you to write perfect essays by grading your work, giving
          feedback, and showing you exactly how an examiner would mark it. Then it helps you revise to make
          your work better and better.
        </p>
      )
    },
    {
      question: "How does Pulc.ai work?",
      answer: (
        <p>
          Pulc uses AI to give students feedback on their work, giving them personalized exam-focused feedback,
          and to help teachers give more personalised feedback to their students in less time.
          Users submit their answers, Pulc reads it and provides detailed feedback, and then helps them revise
          it to improve their work.
        </p>
      )
    },
    {
      question: "What subjects does Pulc.ai support?",
      answer: (
        <>
          <p>Pulc currently supports:</p>
          <ul>
            <li>English</li>
            {/* Removed Irish */}
            <li>French </li>
            <li>Spanish</li>
            <li>German</li>
            <li>Maths</li>
          </ul>
          <p>We are continuously expanding our subject offerings.</p>
        </>
      )
    },
    {
      question: "How does Pulc.ai help teachers?",
      answer: (
        <>
          <p>Teachers can use Pulc to:</p>
          <ul>
            <li>Correct a whole class rapidly</li>
            <li>Convert handwritten answers to text</li>
            <li>Accept/reject Pulc's auto-generated comments</li>
            <li>Add their own personalized feedback</li>
            <li>Save significant time on grading</li>
          </ul>
          <p>This allows teachers to focus more on teaching.</p>
        </>
      )
    },
    {
      question: "Is Pulc.ai free to use?",
      answer: (
        <p>
          Yes, Pulc is currently completely free to use for students and teachers. We may need to introduce charging at some point in the future, but our goal is to make high-quality educational feedback accessible to all.
        </p>
      )
    },
    {
      question: "I don't see a grade for my essay. Why not?",
      answer: (
        <p>
          Pulc currently only provides grades for English essays. We are working on expanding this to other subjects soon, but currently you can get feedback on your work for other subjects and learn how to improve, but not a precise grade estimate.
        </p>
      )
    },
    // Removed question about group study/classroom activities/OIDE
    {
      question: "How does Pulc.ai handle my data?",
      answer: (
        <>
          <p>We take data privacy very seriously. Pulc.ai is fully compliant with GDPR and other relevant data protection regulations.
            Pulc will never:</p>
          <ul>
            <li>Sell your Personal Data</li>
            <li>Share it with a Third Party for Advertising</li>
            <li>Reveal any Personal or Sensitive Information</li>
          </ul>
          <p>
            We do use submitted essays to improve our model and ensure consistent grading, but this data is fully
            anonymized and stored securely. You always have the right to withdraw your data by emailing us at:
            <a href="mailto:hello@pulc.co.uk"> hello@pulc.co.uk</a>
          </p>
          <p>
            For complete details about how we handle your data, please refer to our <Link to="/privacy">Privacy Policy</Link>.
          </p>
        </>
      )
    },
    {
      question: "Who created Pulc.ai?",
      answer: (
        <>
          <p>Pulc was created by Cillian Ò Fathaigh and Charles Dillon:</p>
          <p>
            <strong>Cillian</strong> is a university lecturer with a PhD from the University of Cambridge.
            He writes papers on philosophy of technology (including AI) and political philosophy, and has worked at
            prestigious universities across Europe. {/* Removed reference to Selling notes for leaving cert */}
          </p>
          <p>
            <strong>Charles</strong> studied mathematics at Trinity College Dublin. He worked in quantitative finance
            for a decade and has taught Maths to students of all ages. He's passionate about using AI to make education more effective,
            creative, and enjoyable.
          </p>
        </>
      )
    },
    {
      question: "What's the mission behind Pulc.ai?",
      answer: (
        <p>
          Our mission is to end education inequality. We believe that AI can help level the playing field in education.
          Currently, many students face pressure to pay for expensive tutoring, learn off large chunks of material, and
          purchase sample essays. We want to change this by making high-quality educational feedback accessible to all
          students, regardless of their background or resources. {/* Removed H1 reference, adjusted grind school wording */}
        </p>
      )
    },

    {
      question: "How can I contact Pulc.ai?",
      answer: (
        <p>
          You can reach us at <a href="mailto:hello@pulc.co.uk">hello@pulc.co.uk</a> for any questions, concerns, or feedback.
        </p>
      )
    },
    {
      question: "What does Pulc mean?",
      answer: (
        <p>
         Pulc is from the Irish word meaning "to cram for an examination". <a href="https://www.teanglann.ie/en/fgb/pulc">https://www.teanglann.ie/en/fgb/pulc</a>
         {/* Kept this as it's the origin of the name, not country specific usage */}
        </p>
      )
    }
  ];

  return (
    <div className="faq-container">
      <div className="back-nav">
        {/* Assuming the UK page links back to a UK landing page, adjust '/uk' as needed */}
        <Link to="/uk" className="nav-button nav-button-primary">← Back</Link>
      </div>

      <h1>Frequently Asked Questions</h1>

      <div className="faq-section">
        {faqItems.map((item, index) => (
          <div key={index} className="faq-item">
            <div
              className={`faq-question ${expandedItems[index] ? 'active' : ''}`}
              onClick={() => toggleItem(index)}
            >
              <h3>{item.question}</h3>
              <div className="faq-toggle-icon">
                {expandedItems[index] ? '−' : '+'}
              </div>
            </div>
            <div className={`faq-answer ${expandedItems[index] ? 'show' : ''}`}>
              {item.answer}
            </div>
          </div>
        ))}
      </div>

      <div className="cta-section">
        <h2>Ready to get started?</h2>
        <div className="cta-buttons">
          {/* Adjust links if UK registration/login paths differ */}
          <Link to="/register" className="nav-button nav-button-secondary">Create an Account</Link>
          <Link to="/login" className="nav-button nav-button-primary">Login</Link>
        </div>
      </div>
    </div>
  );
};

export default FAQ_UK; 